import { useCallback, useMemo } from "react";
import { startOfDay, endOfDay, addMinutes } from "date-fns";
import { cn } from "@/lib/utils";
import { useCalendarViewEvents } from "@/hooks/calendar/useCalendarViewEvents";
import { CalendarDayView } from "./week-view/CalendarDayView";
import type { CalendarEvent, CalendarEventType } from "@/types/calendar-view";

// ---------------------------------------------------------------------------
// CalendarSidebar — self-contained sidebar for ActivityCreateModal
// Shows a single-day view with the user's agenda. Navigate between days.
// Supports a "preview event" that represents the activity being created.
// ---------------------------------------------------------------------------

interface CalendarSidebarPreview {
  type: CalendarEventType;
  startTime?: Date;      // combined date + time from the form
  durationMinutes: number;
  title?: string;
}

interface CalendarSidebarProps {
  userId?: string;
  selectedDate: Date;
  selectedTime?: Date;
  preview?: CalendarSidebarPreview;
  onSlotSelect?: (date: Date) => void;
  onDateChange?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onPreviewResize?: (newDurationMin: number) => void;
  onPreviewMove?: (newStartTime: Date) => void;
  className?: string;
}

const PREVIEW_EVENT_ID = '__preview__';

export const CalendarSidebar = ({
  userId,
  selectedDate,
  selectedTime,
  preview,
  onSlotSelect,
  onDateChange,
  onEventClick,
  onPreviewResize,
  onPreviewMove,
  className,
}: CalendarSidebarProps) => {
  const dayStart = startOfDay(selectedDate);
  const dayEnd = endOfDay(selectedDate);

  const { data: fetchedEvents = [], isLoading } = useCalendarViewEvents({
    userId,
    startDate: dayStart,
    endDate: dayEnd,
  });

  // Merge fetched events with the preview event from the form
  const events = useMemo(() => {
    const base = [...fetchedEvents];

    if (preview?.startTime) {
      const previewEvent: CalendarEvent = {
        id: PREVIEW_EVENT_ID,
        title: preview.title || 'Nova atividade',
        start: preview.startTime,
        end: addMinutes(preview.startTime, preview.durationMinutes || 30),
        type: preview.type,
        status: 'pending',
        source: 'crm',
      };
      base.push(previewEvent);
    }

    return base;
  }, [fetchedEvents, preview]);

  const handleSlotClick = useCallback(
    (startTime: Date) => {
      onSlotSelect?.(startTime);
    },
    [onSlotSelect]
  );

  const handleDateChange = useCallback(
    (newDate: Date) => {
      onDateChange?.(newDate);
    },
    [onDateChange]
  );

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      if (event.id === PREVIEW_EVENT_ID) return; // don't open details for preview
      onEventClick?.(event);
    },
    [onEventClick]
  );

  const handleEventResize = useCallback(
    (eventId: string, newDuration: number) => {
      if (eventId === PREVIEW_EVENT_ID) {
        onPreviewResize?.(newDuration);
      }
    },
    [onPreviewResize]
  );

  const handleEventMove = useCallback(
    (eventId: string, newStartTime: Date) => {
      if (eventId === PREVIEW_EVENT_ID) {
        onPreviewMove?.(newStartTime);
      }
    },
    [onPreviewMove]
  );

  return (
    <div
      className={cn(
        "flex flex-col h-full border-l border-border bg-background",
        className
      )}
    >
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-sm text-muted-foreground">
            Carregando agenda...
          </div>
        </div>
      ) : (
        <CalendarDayView
          date={selectedDate}
          events={events}
          slotHeight={36}
          selectedSlot={selectedTime}
          onSlotClick={handleSlotClick}
          onDateChange={handleDateChange}
          onEventClick={handleEventClick}
          onEventResize={handleEventResize}
          onEventMove={handleEventMove}
        />
      )}
    </div>
  );
};
